import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROADMAP_DATA, FINS_COMPARISON, COMPUTER_COMPARISON, WETSUIT_COMPARISON } from './data/equipmentsData';
import './Equipments.scss';
import images from './assets/images';
import freediver_silhouette from './assets/images/freediver_silhouette.jpg';

const Equipments = () => {
  const [inventory, setInventory] = useState({
    mask: null,
    snorkel: null,
    wetsuit: null,
    neckWeight: null,
    belt: null,
    computer: null,
    fins: null,
    socks: null,
    gloves: null,
    lanyard: null,
    noseclip: null,
    finBag: null,
    antifog: null,
    hats: null
  });
  const [activeSlot, setActiveSlot] = useState(null);
  const [isSharedView, setIsSharedView] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const getChoicesForSlot = (slotKey) => {
    const findProducts = (id) => {
      for (const step of ROADMAP_DATA) {
        if (step.items) {
          const item = step.items.find(i => i.id === id);
          if (item) return item.products;
        }
      }
      return [];
    };

    if (slotKey === 'mask') return [...findProducts('mask'), ...findProducts('evolve')];
    if (slotKey === 'snorkel') return findProducts('snorkel');
    if (slotKey === 'wetsuit') return [
      ...WETSUIT_COMPARISON.pool.products,
      ...WETSUIT_COMPARISON.spring.products,
      ...WETSUIT_COMPARISON.ocean.products
    ];
    if (slotKey === 'neckWeight') return [...findProducts('neck_weight'), ...findProducts('lobster')];
    if (slotKey === 'belt') return findProducts('belt');
    if (slotKey === 'computer') return [
      ...COMPUTER_COMPARISON.mk3i.products,
      ...COMPUTER_COMPARISON.g2.products
    ];
    if (slotKey === 'fins') return [
      ...FINS_COMPARISON.fiberglass.products,
      ...FINS_COMPARISON.carbon.products
    ];
    if (slotKey === 'socks') return [...findProducts('fin_socks'), ...findProducts('ocean_socks')];
    if (slotKey === 'gloves') return findProducts('gloves');
    if (slotKey === 'lanyard') return findProducts('lanyard');
    if (slotKey === 'noseclip') return findProducts('noseclip');
    if (slotKey === 'finBag') return findProducts('fin_bag');
    if (slotKey === 'antifog') return findProducts('antifog');
    if (slotKey === 'hats') return findProducts('hats');
    if (slotKey === 'actioncam') return [...findProducts('camera360'), ...findProducts('actioncam')];
    return [];
  };

  const generateHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  };

  const handleShare = () => {
    const SLOT_ORDER = [
      'mask', 'noseclip', 'snorkel', 'hats', 'neckWeight', 'wetsuit', 'belt', 
      'computer', 'gloves', 'lanyard', 'finBag', 'antifog', 'fins', 'socks', 'actioncam'
    ];
    
    const codes = SLOT_ORDER.map(key => {
      const item = inventory[key];
      if (!item) return '';
      return generateHash(item.name);
    });
    
    if (codes.every(c => c === '')) {
      alert("공유할 장비가 없습니다. 빈 슬롯을 눌러 장비를 먼저 선택해주세요!");
      return;
    }
    
    while(codes.length > 0 && codes[codes.length - 1] === '') {
      codes.pop();
    }
    
    const shareString = codes.join('.');
    const shareUrl = `${window.location.origin}${window.location.pathname}?s=${shareString}`;
    
    if (navigator.share) {
      navigator.share({
        url: shareUrl,
      }).catch((error) => {
        console.log('공유 취소 또는 에러:', error);
      });
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => {
        alert("짧아진 공유 링크가 클립보드에 복사되었습니다! 친구들에게 공유해보세요.");
      }).catch(() => {
        alert("복사에 실패했습니다.");
      });
    }
  };

  const slotNames = {
    mask: "마스크", snorkel: "스노클", wetsuit: "슈트", neckWeight: "넥웨이트",
    belt: "웨이트/벨트", computer: "컴퓨터", fins: "롱핀", socks: "핀삭스",
    gloves: "장갑", noseclip: "노즈클립", lanyard: "랜야드", finBag: "핀 가방", antifog: "안티포그", hats: "모자",
    actioncam: "액션캠"
  };

  const renderSlot = (slotKey, label, top, left) => {
    const item = inventory[slotKey];
    
    const handleSlotClick = () => {
      if (!isSharedView) {
        setActiveSlot(slotKey);
      } else if (item && item.link) {
        window.open(item.link, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <div 
        className={"Inventory-slot"} 
        style={{ top, left, cursor: (isSharedView && (!item || !item.link)) ? 'default' : 'pointer' }} 
        onClick={handleSlotClick}
      >
        {item ? (
          <img src={item.image} alt={label} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
        ) : (
          <span className={"Slot-empty"}>{"?"}</span>
        )}
        <span className={"Slot-label"}>{label}</span>
      </div>
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const params = new URLSearchParams(window.location.search);
    const sParam = params.get('s');
    let hasSharedItems = false;
    const newInventory = { ...inventory };
    
    if (sParam) {
      const SLOT_ORDER = [
        'mask', 'noseclip', 'snorkel', 'hats', 'neckWeight', 'wetsuit', 'belt', 
        'computer', 'gloves', 'lanyard', 'finBag', 'antifog', 'fins', 'socks', 'actioncam'
      ];
      const codes = sParam.split('.');
      
      codes.forEach((codeStr, idx) => {
        if (codeStr !== '' && idx < SLOT_ORDER.length) {
          const slotKey = SLOT_ORDER[idx];
          const choices = getChoicesForSlot(slotKey);
          
          let found = choices.find(c => generateHash(c.name) === codeStr);
          
          if (!found) {
            const index = parseInt(codeStr, 10);
            if (!isNaN(index) && choices[index]) {
              found = choices[index];
            }
          }
          
          if (found) {
            newInventory[slotKey] = found;
            hasSharedItems = true;
          }
        }
      });
    } else {
      // Fallback for old long URLs
      Object.keys(newInventory).forEach(key => {
        const itemName = params.get(key);
        if (itemName) {
          const choices = getChoicesForSlot(key);
          const found = choices.find(c => c.name === itemName);
          if (found) {
            newInventory[key] = found;
            hasSharedItems = true;
          }
        }
      });
    }

    if (hasSharedItems) {
      setInventory(newInventory);
      setIsSharedView(true);
    }
    
    // Proactively preload all images for instant modal rendering
    const allImages = new Set();
    const addImagesFromProducts = (products) => {
      if (products) products.forEach(p => { if (p.image) allImages.add(p.image); });
    };
    
    ROADMAP_DATA.forEach(stage => {
      if (stage.items) {
        stage.items.forEach(item => addImagesFromProducts(item.products));
      }
    });
    addImagesFromProducts(WETSUIT_COMPARISON.pool.products);
    addImagesFromProducts(WETSUIT_COMPARISON.spring.products);
    addImagesFromProducts(WETSUIT_COMPARISON.ocean.products);
    addImagesFromProducts(COMPUTER_COMPARISON.mk3i.products);
    addImagesFromProducts(COMPUTER_COMPARISON.g2.products);
    addImagesFromProducts(FINS_COMPARISON.fiberglass.products);
    addImagesFromProducts(FINS_COMPARISON.carbon.products);
    
    allImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
    
    // Dynamically update Meta Tags for this page
    const originalTitle = document.title;
    document.title = "FreeDive Ai - 나의 프리다이빙 장비 세팅 🤿";
    
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };
    
    setMeta('name', 'description', '나에게 꼭 맞는 프리다이빙 장비를 세팅해보고 친구들과 공유해보세요!');
    setMeta('property', 'og:title', 'FreeDive Ai - 나의 프리다이빙 장비 세팅 🤿');
    setMeta('property', 'og:description', '내가 구성해본 맞춤형 프리다이빙 장비 셋업입니다. 지금 구경해보세요!');
    setMeta('name', 'twitter:title', 'FreeDive Ai - 나의 프리다이빙 장비 세팅 🤿');
    setMeta('name', 'twitter:description', '내가 구성해본 맞춤형 프리다이빙 장비 셋업입니다. 지금 구경해보세요!');

    return () => {
      // Revert on unmount (optional, but good practice for SPAs)
      document.title = originalTitle;
      setMeta('name', 'description', 'Improve your freediving skills with FreeDive Ai. Features customized CO2 & O2 tables, a smart diving logbook, and license tracking for iOS and Android.');
      setMeta('property', 'og:title', 'FreeDive Ai - Ultimate Apnea Training & Logbook App');
      setMeta('property', 'og:description', 'Improve your freediving skills with FreeDive Ai. Features customized CO2 & O2 tables, a smart diving logbook, and license tracking for iOS and Android.');
      setMeta('name', 'twitter:title', 'FreeDive Ai - Ultimate Apnea Training & Logbook App');
      setMeta('name', 'twitter:description', 'Improve your freediving skills with FreeDive Ai. Features customized CO2 & O2 tables, a smart diving logbook, and license tracking for iOS and Android.');
    };
  }, []);

  const renderInventory = () => (
    <div className={"Inventory-section"}>
      {!isSharedView && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button className={"Drawer-close-btn"} onClick={() => setIsDrawerOpen(false)}>{"✕"}</button>
        </div>
      )}
      <div className={"Inventory-header"} style={{ justifyContent: 'center' }}>
        <h2 className={"Inventory-title"} style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
          {isSharedView ? "프리다이버의 장비 셋업" : "나의 인벤토리"}
        </h2>
      </div>
      <p className={"Inventory-desc"} style={{ textAlign: 'center' }}>
        {isSharedView ? "공유받은 맞춤형 프리다이빙 장비 구성입니다." : "물음표 슬롯을 눌러 나만의 장비를 채워보세요!"}
      </p>
      <div className={"Inventory-container"}>
        <img src={freediver_silhouette} alt={"Freediver silhouette"} className={"Inventory-silhouette"} />
        
        {renderSlot('mask', '마스크', '9%', '50%')}
        {renderSlot('noseclip', '노즈클립', '17%', '38%')}
        {renderSlot('hats', '모자', '9%', '62%')}
        {renderSlot('snorkel', '스노클', '20%', '62%')}
        {renderSlot('neckWeight', '넥웨이트', '25%', '50%')}
        {renderSlot('wetsuit', '슈트', '38%', '50%')}
        {renderSlot('belt', '웨이트/벨트', '49%', '50%')}
        {renderSlot('computer', '컴퓨터', '45%', '25%')}
        {renderSlot('gloves', '장갑', '56%', '25%')}
        {renderSlot('lanyard', '랜야드', '49%', '75%')}
        {renderSlot('finBag', '핀 가방', '67%', '74%')}
        {renderSlot('antifog', '안티포그', '67%', '85%')}
        {renderSlot('actioncam', '액션캠', '78%', '80%')}
        {renderSlot('fins', '롱핀', '82%', '60%')}
        {renderSlot('socks', '핀삭스', '82%', '40%')}
      </div>
      {!isSharedView ? (
        <div style={{ display: 'flex', gap: '10px', marginTop: '2rem' }}>
          <button 
            className={"Inventory-share-btn"} 
            style={{ background: '#64748b', marginTop: 0, flex: 1, boxShadow: '0 10px 20px rgba(100, 116, 139, 0.4)' }} 
            onClick={() => {
              if (window.confirm("선택한 장비를 모두 초기화하시겠습니까?")) setInventory({});
            }}>
            {"전체 초기화"}
          </button>
          <button className={"Inventory-share-btn"} style={{ marginTop: 0, flex: 2 }} onClick={handleShare}>{"공유하기"}</button>
        </div>
      ) : (
        <button 
          className={"Inventory-share-btn"} 
          style={{ background: '#3399ff' }} 
          onClick={() => window.location.href = '/equipments'}>
          {"나만의 장비 셋업 만들기 ›"}
        </button>
      )}
    </div>
  );

  return (
    <div className={"Equipments"}>
      <header className={"Equipments-header"}>
        <div className={"Header-content"}>
          <Link to={"/"} className={"Back-button"}>{'← Back to Home'}</Link>
          <img src={images.ic_app} className={"App-logo-small"} alt={"Freediving App Logo"} style={{marginLeft: 'auto'}} />
        </div>
      </header>
      
      <main className={"Equipments-main"}>
        <section className={"Hero-section Equipments-hero"}>
          <h2 className={"Hero-headline"}>{'프리다이빙 장비 큐레이션'}</h2>
          <p className={"Hero-subheadline"}>
            {'다이빙 투어부터 수심 트레이닝까지, 당신의 프리다이빙 여정을 함께할 완벽한 장비 로드맵을 확인하세요.'}
          </p>
        </section>

        {activeSlot && (
          <div className={"Inventory-modal-overlay"} onClick={() => setActiveSlot(null)}>
            <div className={"Inventory-modal"} onClick={e => e.stopPropagation()}>
              <h3 className={"Modal-title"}>{"장비를 선택하세요"}</h3>
              <div className={"Modal-grid"}>
                {getChoicesForSlot(activeSlot).map((choice, idx) => {
                  const isSelected = inventory[activeSlot]?.name === choice.name;
                  return (
                  <div key={idx} className={`Modal-item ${isSelected ? 'selected' : ''}`} onClick={() => {
                    setInventory(prev => ({ ...prev, [activeSlot]: choice }));
                    setActiveSlot(null);
                  }}>
                    <div className={"Modal-item-image-wrapper"}>
                      <img src={choice.image} alt={choice.name} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                    </div>
                    <p className={"Modal-item-name"}>{choice.name}</p>
                  </div>
                )})}
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                {inventory[activeSlot] && (
                  <button className={"Modal-close-btn"} style={{ background: '#ef4444' }} onClick={() => {
                    setInventory(prev => {
                      const newInv = { ...prev };
                      delete newInv[activeSlot];
                      return newInv;
                    });
                    setActiveSlot(null);
                  }}>{"선택 해제"}</button>
                )}
                <button className={"Modal-close-btn"} onClick={() => setActiveSlot(null)}>{"닫기"}</button>
              </div>
            </div>
          </div>
        )}

        {isSharedView ? (
          <>
            {renderInventory()}
            <div className={"Shared-list-section"}>
              <h3 className={"Shared-list-title"}>{"친구가 선택한 장비 목록"}</h3>
              <div className={"Shared-list-grid"}>
                {Object.entries(inventory).map(([key, item]) => {
                  if (!item) return null;
                  return (
                    <div 
                      key={key} 
                      className={"Shared-list-item"}
                      onClick={() => item.link && window.open(item.link, '_blank', 'noopener,noreferrer')}
                      style={{ cursor: item.link ? 'pointer' : 'default' }}
                    >
                      <img src={item.image} alt={item.name} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                      <div className={"Shared-list-item-info"}>
                        <span className={"Shared-list-item-slot"}>{slotNames[key]}</span>
                        <span className={"Shared-list-item-name"}>{item.name}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button className={"Start-my-own-btn"} onClick={() => {
                window.location.href = window.location.pathname;
              }}>{"나도 내 장비 세팅해보기 🤿"}</button>
            </div>
          </>
        ) : (
          <>
            <button className={"Inventory-fab"} onClick={() => setIsDrawerOpen(true)}>
              {"🎒 나의 인벤토리"}
            </button>
            
            <div className={`Drawer-overlay ${isDrawerOpen ? 'open' : ''}`} onClick={() => setIsDrawerOpen(false)} />
            <div className={`Inventory-drawer ${isDrawerOpen ? 'open' : ''}`}>
              {renderInventory()}
            </div>
            
            <section className={"Roadmap-section"}>
              {ROADMAP_DATA.map((stage, index) => (
            <div key={index} className={"Roadmap-stage"}>
              <div className={"Stage-badge"}>{stage.step}</div>
              <h3 className={"Stage-title"}>{stage.title}</h3>
              <h4 className={"Stage-subtitle"}>{stage.subtitle}</h4>
              <p className={"Stage-desc"}>{stage.description}</p>

              {stage.isWetsuit && (
                <div className={"Fins-comparison"}>
                  <div className={"Fin-card fiberglass"}>
                    <h5 className={"Fin-type"}>{WETSUIT_COMPARISON.pool.title}</h5>
                    <p className={"Fin-appeal"}>{WETSUIT_COMPARISON.pool.appeal}</p>
                    <ul className={"Fin-features"}>
                      {WETSUIT_COMPARISON.pool.pros.map((p, i) => <li key={i} className={"pro"}>{p}</li>)}
                      {WETSUIT_COMPARISON.pool.cons.map((c, i) => <li key={i} className={"con"}>{c}</li>)}
                    </ul>
                    
                    {WETSUIT_COMPARISON.pool.products && (
                      <div className={"Product-list"} style={{marginTop: '1.5rem'}}>
                        {WETSUIT_COMPARISON.pool.products.map((prod, pIdx) => (
                          <a href={prod.link} target="_blank" rel="noreferrer" key={pIdx} className={"Product-row"}>
                            <img src={prod.image} alt={prod.name} className={"Product-thumb"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                            <div className={"Product-details"}>
                              {prod.badge && (
                                <span className={"Product-badge"} style={{ backgroundColor: prod.badgeColor }}>
                                  {prod.badge}
                                </span>

                              )}
                              <h5 className={"Product-title"}>{prod.name}</h5>
                            </div>
                            <div className={"Product-arrow"}>{'›'}</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={"Fin-card fiberglass"}>
                    <h5 className={"Fin-type"}>{WETSUIT_COMPARISON.spring.title}</h5>
                    <p className={"Fin-appeal"}>{WETSUIT_COMPARISON.spring.appeal}</p>
                    <ul className={"Fin-features"}>
                      {WETSUIT_COMPARISON.spring.pros.map((p, i) => <li key={i} className={"pro"}>{p}</li>)}
                      {WETSUIT_COMPARISON.spring.cons.map((c, i) => <li key={i} className={"con"}>{c}</li>)}
                    </ul>
                    
                    {WETSUIT_COMPARISON.spring.products && (
                      <div className={"Product-list"} style={{marginTop: '1.5rem'}}>
                        {WETSUIT_COMPARISON.spring.products.map((prod, pIdx) => (
                          <a href={prod.link} target="_blank" rel="noreferrer" key={pIdx} className={"Product-row"}>
                            <img src={prod.image} alt={prod.name} className={"Product-thumb"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                            <div className={"Product-details"}>
                              {prod.badge && (
                                <span className={"Product-badge"} style={{ backgroundColor: prod.badgeColor }}>
                                  {prod.badge}
                                </span>
                              )}
                              <h5 className={"Product-title"}>{prod.name}</h5>
                            </div>
                            <div className={"Product-arrow"}>{'›'}</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={"Fin-card carbon"}>
                    <h5 className={"Fin-type"}>{WETSUIT_COMPARISON.ocean.title}</h5>
                    <p className={"Fin-appeal"}>{WETSUIT_COMPARISON.ocean.appeal}</p>
                    <ul className={"Fin-features"}>
                      {WETSUIT_COMPARISON.ocean.pros.map((p, i) => <li key={i} className={"pro"}>{p}</li>)}
                      {WETSUIT_COMPARISON.ocean.cons.map((c, i) => <li key={i} className={"con"}>{c}</li>)}
                    </ul>
                    
                    {WETSUIT_COMPARISON.ocean.products && (
                      <div className={"Product-list"} style={{marginTop: '1.5rem'}}>
                        {WETSUIT_COMPARISON.ocean.products.map((prod, pIdx) => (
                          <a href={prod.link} target="_blank" rel="noreferrer" key={pIdx} className={"Product-row"}>
                            <img src={prod.image} alt={prod.name} className={"Product-thumb"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                            <div className={"Product-details"}>
                              {prod.badge && (
                                <span className={"Product-badge"} style={{ backgroundColor: prod.badgeColor }}>
                                  {prod.badge}
                                </span>
                              )}
                              <h5 className={"Product-title"}>{prod.name}</h5>
                            </div>
                            <div className={"Product-arrow"}>{'›'}</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {stage.isFins && (
                <div className={"Fins-comparison"}>
                  <div className={"Fin-card fiberglass"}>
                    <h5 className={"Fin-type"}>{FINS_COMPARISON.fiberglass.title}</h5>
                    <p className={"Fin-appeal"}>{FINS_COMPARISON.fiberglass.appeal}</p>
                    <ul className={"Fin-features"}>
                      {FINS_COMPARISON.fiberglass.pros.map((p, i) => <li key={i} className={"pro"}>{p}</li>)}
                      {FINS_COMPARISON.fiberglass.cons.map((c, i) => <li key={i} className={"con"}>{c}</li>)}
                    </ul>
                    
                    {FINS_COMPARISON.fiberglass.products && (
                      <div className={"Product-list"} style={{marginTop: '1.5rem'}}>
                        {FINS_COMPARISON.fiberglass.products.map((prod, pIdx) => (
                          <a href={prod.link} target="_blank" rel="noreferrer" key={pIdx} className={"Product-row"}>
                            <img src={prod.image} alt={prod.name} className={"Product-thumb"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                            <div className={"Product-details"}>
                              {prod.badge && (
                                <span className={"Product-badge"} style={{ backgroundColor: prod.badgeColor }}>
                                  {prod.badge}
                                </span>
                              )}
                              <h5 className={"Product-title"}>{prod.name}</h5>
                            </div>
                            <div className={"Product-arrow"}>{'›'}</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={"Fin-card carbon"}>
                    <h5 className={"Fin-type"}>{FINS_COMPARISON.carbon.title}</h5>
                    <p className={"Fin-appeal"}>{FINS_COMPARISON.carbon.appeal}</p>
                    <ul className={"Fin-features"}>
                      {FINS_COMPARISON.carbon.pros.map((p, i) => <li key={i} className={"pro"}>{p}</li>)}
                      {FINS_COMPARISON.carbon.cons.map((c, i) => <li key={i} className={"con"}>{c}</li>)}
                    </ul>
                    
                    {FINS_COMPARISON.carbon.products && (
                      <div className={"Product-list"} style={{marginTop: '1.5rem'}}>
                        {FINS_COMPARISON.carbon.products.map((prod, pIdx) => (
                          <a href={prod.link} target="_blank" rel="noreferrer" key={pIdx} className={"Product-row"}>
                            <img src={prod.image} alt={prod.name} className={"Product-thumb"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                            <div className={"Product-details"}>
                              {prod.badge && (
                                <span className={"Product-badge"} style={{ backgroundColor: prod.badgeColor }}>
                                  {prod.badge}
                                </span>
                              )}
                              <h5 className={"Product-title"}>{prod.name}</h5>
                            </div>
                            <div className={"Product-arrow"}>{'›'}</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {stage.isComputer && (
                <div className={"Fins-comparison"}>
                  <div className={"Fin-card fiberglass"}>
                    <h5 className={"Fin-type"}>{COMPUTER_COMPARISON.g2.title}</h5>
                    <p className={"Fin-appeal"}>{COMPUTER_COMPARISON.g2.appeal}</p>
                    <ul className={"Fin-features"}>
                      {COMPUTER_COMPARISON.g2.pros.map((p, i) => <li key={i} className={"pro"}>{p}</li>)}
                      {COMPUTER_COMPARISON.g2.cons.map((c, i) => <li key={i} className={"con"}>{c}</li>)}
                    </ul>
                    
                    {COMPUTER_COMPARISON.g2.products && (
                      <div className={"Product-list"} style={{marginTop: '1.5rem'}}>
                        {COMPUTER_COMPARISON.g2.products.map((prod, pIdx) => (
                          <a href={prod.link} target="_blank" rel="noreferrer" key={pIdx} className={"Product-row"}>
                            <img src={prod.image} alt={prod.name} className={"Product-thumb"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                            <div className={"Product-details"}>
                              {prod.badge && (
                                <span className={"Product-badge"} style={{ backgroundColor: prod.badgeColor }}>
                                  {prod.badge}
                                </span>
                              )}
                              <h5 className={"Product-title"}>{prod.name}</h5>
                            </div>
                            <div className={"Product-arrow"}>{'›'}</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={"Fin-card carbon"}>
                    <h5 className={"Fin-type"}>{COMPUTER_COMPARISON.mk3i.title}</h5>
                    <p className={"Fin-appeal"}>{COMPUTER_COMPARISON.mk3i.appeal}</p>
                    <ul className={"Fin-features"}>
                      {COMPUTER_COMPARISON.mk3i.pros.map((p, i) => <li key={i} className={"pro"}>{p}</li>)}
                      {COMPUTER_COMPARISON.mk3i.cons.map((c, i) => <li key={i} className={"con"}>{c}</li>)}
                    </ul>
                    
                    {COMPUTER_COMPARISON.mk3i.products && (
                      <div className={"Product-list"} style={{marginTop: '1.5rem'}}>
                        {COMPUTER_COMPARISON.mk3i.products.map((prod, pIdx) => (
                          <a href={prod.link} target="_blank" rel="noreferrer" key={pIdx} className={"Product-row"}>
                            <img src={prod.image} alt={prod.name} className={"Product-thumb"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                            <div className={"Product-details"}>
                              {prod.badge && (
                                <span className={"Product-badge"} style={{ backgroundColor: prod.badgeColor }}>
                                  {prod.badge}
                                </span>
                              )}
                              <h5 className={"Product-title"}>{prod.name}</h5>
                            </div>
                            <div className={"Product-arrow"}>{'›'}</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className={"Items-grid"}>
                {stage.items.map((item, idx) => (
                  <div key={idx} className={`Item-card ${item.type}`}>
                    {!item.products && (
                      <div className={"Item-image-placeholder"}>
                        <span>{item.name}{' 사진'}</span>
                      </div>
                    )}
                    <div className={"Item-info"}>
                      <h4 className={"Item-name"}>{item.name}</h4>
                      <p className={"Item-desc"}>{item.desc}</p>
                      
                      {item.products ? (
                        <div className={"Product-list"}>
                          {item.products.map((prod, pIdx) => (
                            <a href={prod.link} target="_blank" rel="noreferrer" key={pIdx} className={"Product-row"}>
                              <img src={prod.image} alt={prod.name} className={"Product-thumb"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = images.ic_app; }} />
                              <div className={"Product-details"}>
                                {prod.badge && (
                                  <span className={"Product-badge"} style={{ backgroundColor: prod.badgeColor }}>
                                    {prod.badge}
                                  </span>
                                )}
                                <h5 className={"Product-title"}>{prod.name}</h5>
                              </div>
                              <div className={"Product-arrow"}>
                                {'›'}
                              </div>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <button className={"Buy-button"} disabled>
                          {'추천 장비 보기 (준비 중)'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
        </>
        )}

        <footer className={"Equipments-footer"}>
          <p>
            {'본 페이지는 제작자의 개인적인 기준과 취향을 바탕으로 만들어진 큐레이션입니다.'}
            <br />
            {'링크가 깨져있거나 잘못된 부분, 혹은 장비 큐레이션에 대한 다른 소중한 의견이 있으시다면 아래 메일로 제보 부탁드립니다.'}
          </p>
          <a href={"mailto:kevin85.oh@gmail.com"} className={"Footer-email"}>
            {'📧 kevin85.oh@gmail.com'}
          </a>
        </footer>
      </main>
    </div>
  );
};

export default Equipments;
